/**
 * JSON-LD structured data builders.
 * Pure functions returning schema.org objects — render them through
 * react-helmet-async (the Seo component's jsonLd prop or a Helmet script tag),
 * never by manually mutating document.head.
 */

const SITE_URL = "https://managertable.com";

export interface ManagerJsonLdInput {
  managerName: string;
  clubName: string;
  slug?: string | null;
  startDate?: string;
  endDate?: string | null;
  nationality?: string | null;
  birthDate?: string | null;
  description?: string;
  pastClubs?: Array<{ name: string; startDate: string; endDate?: string | null }>;
  mpi?: { score: number; updated?: string | null } | null;
}

/** Person schema for an individual manager profile view. */
export function managerJsonLd({
  managerName,
  clubName,
  slug,
  startDate,
  nationality,
  birthDate,
  description,
  pastClubs,
  mpi,
}: ManagerJsonLdInput): Record<string, unknown> {
  const profilePath = slug ? `/manager/${slug}` : `/?manager=${encodeURIComponent(managerName.toLowerCase().replace(/\s+/g, "-"))}`;
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: managerName,
    jobTitle: "Football Manager",
    worksFor: {
      "@type": "SportsTeam",
      name: clubName,
      sport: "Football",
    },
    url: `${SITE_URL}${profilePath}`,
    ...(nationality && { nationality: { "@type": "Country", name: nationality } }),
    ...(birthDate && { birthDate }),
    ...(description && { description }),
    ...(pastClubs && pastClubs.length > 0 && {
      affiliation: pastClubs.map((c) => ({
        "@type": "OrganizationRole",
        roleName: "Manager",
        startDate: c.startDate,
        ...(c.endDate && { endDate: c.endDate }),
        affiliation: { "@type": "SportsTeam", name: c.name, sport: "Football" },
      })),
    }),
    ...(mpi && {
      additionalProperty: {
        "@type": "PropertyValue",
        name: "ManagerTable Pressure Index",
        value: mpi.score,
        minValue: 0,
        maxValue: 100,
        ...(mpi.updated && { dateModified: mpi.updated }),
      },
    }),
    ...(startDate && {
      hasOccupation: {
        "@type": "Occupation",
        name: "Football Manager",
        occupationalCategory: "27-2022.00",
      },
    }),
  };
}

/** ItemList schema for MPI rankings tables. */
export function hotSeatTableJsonLd(
  leagueName: string,
  managers: Array<{ name: string; clubName: string; score: number }>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${leagueName} MPI Rankings`,
    description: `Current ManagerTable Pressure Index (MPI) rankings for ${leagueName}`,
    numberOfItems: managers.length,
    itemListElement: managers.slice(0, 10).map((manager, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Person",
        name: manager.name,
        jobTitle: "Football Manager",
        worksFor: {
          "@type": "SportsTeam",
          name: manager.clubName,
        },
      },
    })),
  };
}

/** FAQ schema for common questions about ManagerTable. */
export function faqJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is the ManagerTable Pressure Index (MPI)?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The MPI (ManagerTable Pressure Index) is a metric from 0-100 that measures how much pressure a football manager is under. It considers factors like points per game, recent form, current league position vs expectations, and tenure length. A score above 75 indicates a manager is in serious danger of losing their job.",
        },
      },
      {
        "@type": "Question",
        name: "How is the Manager Table calculated?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The Manager Table shows the league standings based only on matches played during a specific manager's tenure. This allows you to compare how different clubs performed under their current or past managers.",
        },
      },
      {
        "@type": "Question",
        name: "Which leagues are covered?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Manager Table covers the top European football leagues including the Premier League, La Liga, Bundesliga, Serie A, Ligue 1, and more. Data is updated regularly throughout the season.",
        },
      },
    ],
  };
}

/** WebSite schema for the whole site. */
export function websiteJsonLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ManagerTable",
    description:
      "Analyze football league tables by manager tenure. Track MPI (ManagerTable Pressure Index), pressure trends, and manager performance across European leagues.",
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/?manager={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** BreadcrumbList schema. Items are [name, path] pairs. */
export function breadcrumbJsonLd(items: Array<[string, string]>): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(([name, path], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: `${SITE_URL}${path}`,
    })),
  };
}
